import React, { useState, useEffect } from 'react';
import {
    Bot, Database, MessageSquare, Globe, Zap, Headphones, Users,
    Ticket, CheckCircle, Truck, Power, Code, BarChart3, GitBranch,
    PlayCircle, ArrowRight, ShoppingCart, MessageCircle, Image, Search, Package,
    Star, AlertCircle, UserCheck, MessageSquareText, ChevronLeft, ChevronRight,
    Terminal, Cpu, Sparkles, ShieldCheck, Facebook, Instagram, Mail, Clock, Shield, Tag, Inbox, Filter,
    TrendingUp, PieChart, PenTool, Calendar, Share2, Code2,
    Crosshair, LayoutDashboard, LayoutPanelLeft, Webhook, MousePointer2, ThumbsUp, ThumbsDown
} from 'lucide-react';


const workflowsData = [
    {
        id: "ticket",
        category: "Ticket",
        title: "Ticket Automation",
        description: "Orchestrate complex business logic without writing a single line of code. Our visual workflow builder automates repetitive tasks.",
        nodes: [
            { icon: <PlayCircle />, label: "Trigger" },
            { icon: <Ticket />, label: "Create Ticket" },
            { icon: <Users />, label: "Notify Human" },
            { icon: <Code />, label: "API Call" }
        ],
        benefits: ["No-Code Drag & Drop", "300+ Integrations", "Error Handling", "Performance Metrics"]
    },
    {
        id: "abandoned",
        category: "Recovery",
        title: "Abandoned Checkout",
        description: "Automatically recover lost sales by messaging customers on WhatsApp when they leave items in their cart.",
        nodes: [
            { icon: <ShoppingCart />, label: "Shopify Abandon" },
            { icon: <MessageCircle />, label: "WhatsApp" },
            { icon: <Sparkles />, label: "Personalize" },
            { icon: <TrendingUp />, label: "Increase Sales" }
        ],
        benefits: ["Sales Recovery", "Smart Reminders", "Direct Re-engagement", "Shopify Sync"]
    },
    {
        id: "order",
        category: "Order",
        title: "Order Confirmation",
        description: "Automate your sales pipeline from order placement to customer confirmation. Keep your customers informed at every step of their journey automatically.",
        nodes: [
            { icon: <ShoppingCart />, label: "Shopify Order" },
            { icon: <MessageCircle />, label: "WhatsApp" },
            { icon: <CheckCircle />, label: "Confirm" },
            { icon: <Package />, label: "Update Stock" }
        ],
        benefits: ["Instant Notifications", "Abandoned Carts", "Automated Updates", "Multi-Platform Sync"]
    },
    {
        id: "delivery",
        category: "Delivery",
        title: "Delivery Support",
        description: "Handle logistics inquiries instantly using live data. Connect directly to delivery partners to provide real-time tracking without manual intervention.",
        nodes: [
            { icon: <MessageSquare />, label: "User Inquiry" },
            { icon: <Truck />, label: "Pull Logistics" },
            { icon: <Search />, label: "Track Package" },
            { icon: <Globe />, label: "Send Update" }
        ],
        benefits: ["Real-time Tracking", "Zero Wait Time", "Carrier Integration", "Auto Follow-ups"]
    },
    {
        id: "visual",
        category: "Search",
        title: "Visual Search AI",
        description: "Enable customers to find what they need using images. Our AI analyzes uploaded photos to identify products and provide instant detailed specifications.",
        nodes: [
            { icon: <Image />, label: "Upload Photo" },
            { icon: <Bot />, label: "AI Analysis" },
            { icon: <Search />, label: "Match Products" },
            { icon: <BarChart3 />, label: "Send Details" }
        ],
        benefits: ["Image Recognition", "Instant ID", "Boost Conversions", "Seamless Discovery"]
    },
    {
        id: "feedback",
        category: "Feedback",
        title: "Feedback Collection",
        description: "Automatically gather customer insights after every interaction. Capture ratings and feedback to continuously improve your service quality.",
        nodes: [
            { icon: <MessageSquareText />, label: "End Session" },
            { icon: <Star />, label: "Send Poll" },
            { icon: <BarChart3 />, label: "Analyze" },
            { icon: <Database />, label: "Store Insights" }
        ],
        benefits: ["Automated CSAT", "Sentiment Analysis", "Live Dashboards", "Quality Control"]
    },
    {
        id: "handoff",
        category: "Handoff",
        title: "Human Escalation",
        description: "Never let a customer get frustrated. Our AI detects complex queries or negative sentiment and instantly notifies your team for a seamless transition.",
        nodes: [
            { icon: <Bot />, label: "Detect Need" },
            { icon: <AlertCircle />, label: "Flag Promptly" },
            { icon: <Users />, label: "Notify Agent" },
            { icon: <UserCheck />, label: "Live Connect" }
        ],
        benefits: ["Sentiment Detection", "Zero Drop-off", "Contextual Handoff", "Priority Queues"]
    }
];

const agentsData = [
    {
        id: "refund",
        title: "Refund AI Agent",
        instruction: "You are a Refund Agent. Ask the user about the issue, analyze if it meets criteria, and report to Admin.",
        steps: ["Listen", "Analyze", "Report"],
        icon: <ShieldCheck />
    },
    {
        id: "order",
        title: "Order Booking AI Agent",
        instruction: "You are an Order Booking Agent. Check inventory on Shopify, ask for Name/Address, and place the order.",
        steps: ["Check Stock", "Collect Data", "Place Order"],
        icon: <ShoppingCart />
    },
    {
        id: "wholesale",
        title: "Wholesale Order AI Agent",
        instruction: "Verify inventory for bulk orders. Notify Admin if empty. Verify advance payment and finalize wholesale order.",
        steps: ["Verify Bulk", "Notify Admin", "Secure Payment"],
        icon: <Cpu />
    }
];

const socialChatsData = [
    { platform: 'WhatsApp', user: 'Faizan Ahmed', message: 'Hey, I need help with my order #1234', time: 'Just now', color: 'text-green-500', icon: <MessageCircle /> },
    { platform: 'Instagram', user: 'Sarah Malik', message: 'Do you have this in blue?', time: '2m ago', color: 'text-pink-500', icon: <Instagram /> },
    { platform: 'Messenger', user: 'Imran Khan', message: 'When will the new stock arrive?', time: '5m ago', color: 'text-blue-500', icon: <MessageSquare /> },
    { platform: 'Gmail', user: 'Zubair Shah', message: 'Re: Refund Request #SH882', time: '12m ago', color: 'text-red-500', icon: <Mail /> },
    { platform: 'Facebook', user: 'Ali Raza', message: 'Great product! How do I order?', time: '20m ago', color: 'text-blue-700', icon: <Facebook /> },
];

const ticketsData = [
    { id: 'TIC-882', title: 'Payment Failed', priority: 'High', status: 'AI Handled', sentiment: 'Frustrated', type: 'Payment' },
    { id: 'TIC-883', title: 'Address Update', priority: 'Medium', status: 'In Progress', sentiment: 'Neutral', type: 'Shipping' },
    { id: 'TIC-884', title: 'Wrong Size Delivered', priority: 'Critical', status: 'Escalated', sentiment: 'Angry', type: 'Returns' },
];

const analyticsData = [
    { label: 'Chat Volume', value: '85%', trend: '+12%', color: 'bg-blue-500' },
    { label: 'AI Resolution', value: '94%', trend: '+5%', color: 'bg-green-500' },
    { label: 'Customer CSAT', value: '4.8', trend: '+0.2', color: 'bg-purple-500' },
];

const marketingData = [
    { platform: 'Instagram', action: 'New Post', content: 'Transform your business with AI...', status: 'Scheduled' },
    { platform: 'Facebook', action: 'Ad Campaign', content: '20% Lead Increase', status: 'Active' },
];

const integrationIcons = [
    { src: '/facebook.jpg', label: 'Facebook' },
    { src: '/instagram.jpg', label: 'Instagram' },
    { src: '/gmail.jpg', label: 'Gmail' },
    { src: '/wa.jpg', label: 'WhatsApp' },
    { src: '/telegram.jpg', label: 'Telegram' },
    { src: '/slack.jpg', label: 'Slack' },
    { src: '/shopify.jpg', label: 'Shopify' },
    { src: '/zapier.jpg', label: 'Zapier' },
    { src: '/wordpress.jpg', label: 'WordPress' },
    { src: '/postex.webp', label: 'PostEx' },
];

const Services = () => {
    const [activeFlow, setActiveFlow] = useState(0);
    const [activeAgent, setActiveAgent] = useState(0);
    const [activeChat, setActiveChat] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [typingText, setTypingText] = useState("");
    const [agentStatus, setAgentStatus] = useState("typing");

    // Workflow cycling
    useEffect(() => {
        if (isPaused) return;
        const timer = setInterval(() => {
            setActiveFlow((prev) => (prev + 1) % workflowsData.length);
        }, 3500);
        return () => clearInterval(timer);
    }, [isPaused]);

    // Social Chat cycling
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveChat((prev) => (prev + 1) % socialChatsData.length);
        }, 3000);
        return () => clearInterval(timer);
    }, []);

    // Agent cycling & typing simulation
    useEffect(() => {
        let text = agentsData[activeAgent].instruction;
        let charIndex = 0;
        setAgentStatus("typing");
        setTypingText("");

        const typeInterval = setInterval(() => {
            if (charIndex <= text.length) {
                setTypingText(text.slice(0, charIndex));
                charIndex++;
            } else {
                clearInterval(typeInterval);
                setAgentStatus("creating");
                setTimeout(() => {
                    setAgentStatus("active");
                    setTimeout(() => {
                        setActiveAgent((prev) => (prev + 1) % agentsData.length);
                    }, 4000);
                }, 1500);
            }
        }, 40);

        return () => clearInterval(typeInterval);
    }, [activeAgent]);

    const nextFlow = () => setActiveFlow((prev) => (prev + 1) % workflowsData.length);
    const prevFlow = () => setActiveFlow((prev) => (prev - 1 + workflowsData.length) % workflowsData.length);

    const flow = workflowsData[activeFlow];
    const agent = agentsData[activeAgent];

    return (
        <section id="solutions" className="w-full py-20 bg-slate-50 relative overflow-hidden scroll-mt-24">
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <div className="absolute top-[-10%] right-[-5%] w-[400px] h-[400px] bg-blue-100/30 rounded-full blur-3xl"></div>
                <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-blue-50/50 rounded-full blur-3xl"></div>
            </div>

            <div className="max-w-6xl mx-auto px-6 md:px-10 relative z-10">
                <div className="flex flex-col items-center gap-16">
                    <div className="flex flex-col items-center gap-3 text-center">
                        <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-[10px]">What We Do</span>
                        <h2 className="text-2xl md:text-5xl font-extrabold text-slate-900 tracking-tight">Enterprise-Grade AI Solutions</h2>
                    </div>

                    <div className="flex flex-col gap-12 w-full max-w-5xl">
                        {/* AI Chatbot Card */}
                        <div className="bg-white/80 backdrop-blur-sm border border-white rounded-[2.5rem] p-4 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_60px_rgba(59,130,246,0.15)] hover:border-blue-200 transition-all duration-500 group relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-600"></div>
                            <div className="flex flex-col lg:flex-row items-center gap-6 md:gap-12">
                                <div className="w-full lg:w-[45%] flex-shrink-0">
                                    <h3 className="text-lg md:text-2xl font-bold text-slate-900 mb-4 md:mb-8 text-center flex items-center justify-center gap-2">
                                        <Bot className="w-5 h-5 text-blue-600" />
                                        AI Chatbot
                                    </h3>
                                    <DiagramPlaceholder />
                                </div>
                                <div className="flex-1 flex flex-col gap-6 text-left">
                                    <div className="flex flex-col gap-4">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-[10px]">AI Solutions</span>
                                            <h4 className="text-xl md:text-2xl font-bold text-slate-900">Intelligent Customer Engagement</h4>
                                        </div>
                                        <p className="text-slate-600 text-sm md:text-lg leading-relaxed">Our AI Chatbot acts as your 24/7 digital concierge, trained on your unique business data for instant, accurate responses.</p>
                                    </div>
                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                                        {["99% Resolution Accuracy", "Multi-Channel Deployment", "Instant FAQ Training", "Live Human Handoff"].map(b => <BenefitItem key={b} label={b} />)}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Animated Workflows Card */}
                        <div
                            className="bg-white/80 backdrop-blur-sm border border-white rounded-[2.5rem] p-4 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_60px_rgba(59,130,246,0.15)] transition-all duration-500 group relative overflow-hidden"
                            onMouseEnter={() => setIsPaused(true)}
                            onMouseLeave={() => setIsPaused(false)}
                        >
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-600"></div>

                            <div className="flex flex-col lg:flex-row-reverse items-center gap-6 md:gap-12">
                                {/* Visual Side */}
                                <div className="w-full lg:w-[45%] flex-shrink-0 h-[240px] md:h-[300px] flex flex-col">
                                    <div className="flex justify-center gap-1 md:gap-1 mb-4 md:mb-6 overflow-x-auto py-1 no-scrollbar">
                                        {["Ticket", "Recovery", "Order", "Delivery", "Search", "Feedback", "Handoff"].map((cat) => (
                                            <WorkflowBadge key={cat} label={cat} active={flow.category === cat} />
                                        ))}
                                    </div>

                                    <div className="relative flex-1 flex items-center justify-center min-h-[180px] md:min-h-[220px]">
                                        <div className="absolute inset-0 bg-blue-50/50 rounded-2xl z-0" style={{
                                            backgroundImage: 'radial-gradient(circle, #3b82f6 1px, transparent 1px)',
                                            backgroundSize: '16px 16px',
                                            maskImage: 'radial-gradient(ellipse at center, black 50%, transparent 90%)'
                                        }}></div>

                                        <svg className="absolute inset-0 w-full h-full z-[1] pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                                            <path d="M 23 50 L 30 55 M 48 55 L 55 60 M 73 60 L 80 53" stroke="#3b82f6" strokeWidth="1" fill="none" opacity="0.4" />
                                        </svg>

                                        {flow.nodes.map((node, i) => {
                                            const pos = ["left-[2%] top-[50%]", "left-[28%] top-[55%]", "left-[54%] top-[60%]", "left-[80%] top-[53%]"];
                                            return (
                                                <div key={`${flow.id}-${i}`} className={`absolute ${pos[i]} w-[22%] sm:w-[18%] -translate-y-1/2 z-10`}>
                                                    <Node icon={node.icon} label={node.label} active />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Content Side */}
                                <div className="flex-1 flex flex-col gap-3 md:gap-6 text-left">
                                    <div className="min-h-[120px] md:min-h-[160px] flex flex-col gap-3 md:gap-4" key={flow.id}>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs md:text-sm">AUTOMATION WORKFLOWS</span>
                                            <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
                                                <span className="p-2 bg-blue-50 rounded-lg"><GitBranch className="w-6 h-6 text-blue-600" /></span>
                                                {flow.title}
                                            </h3>
                                        </div>
                                        <p className="text-xs md:text-lg leading-relaxed text-slate-600">{flow.description}</p>
                                    </div>

                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 md:gap-4" key={`benefits-${flow.id}`}>
                                        {flow.benefits.map((b) => <BenefitItem key={b} label={b} />)}
                                    </ul>

                                    <div className="flex items-center justify-between mt-2 pt-2 md:mt-8 md:pt-8 border-t border-slate-100">
                                        <div className="flex items-center gap-4">
                                            <button onClick={prevFlow} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 shadow-sm hover:border-blue-500 hover:text-blue-600 transition-all font-bold text-sm text-slate-600">
                                                <ChevronLeft className="w-4 h-4" />
                                                Back
                                            </button>
                                            <div className="hidden md:flex gap-2">
                                                {workflowsData.map((_, i) => (
                                                    <button key={i} onClick={() => setActiveFlow(i)} className={`h-2 rounded-full transition-all duration-500 ${i === activeFlow ? 'w-8 bg-blue-600' : 'w-2 bg-blue-200'}`} />
                                                ))}
                                            </div>
                                            <button onClick={nextFlow} className="flex items-center gap-2 px-5 py-2 rounded-xl bg-blue-600 shadow-[0_4px_12px_rgba(59,130,246,0.3)] hover:bg-blue-700 transition-all font-bold text-sm text-white">
                                                Next
                                                <ChevronRight className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <span className="hidden sm:inline-block text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">Flow {activeFlow + 1} / {workflowsData.length}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mini Feature Cards Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <FeatureMiniCard
                                icon={<Crosshair />}
                                title="Dynamic Data Sync"
                                description="Keep your data up-to-date across platforms with real-time synchronization."
                            >
                                <DataSyncMockup />
                            </FeatureMiniCard>
                            <FeatureMiniCard
                                icon={<LayoutDashboard />}
                                title="Analytics Dashboard"
                                description="Gain insights with real-time analytics on performance and customer behavior."
                            >
                                <AnalyticsMockup />
                            </FeatureMiniCard>
                            <FeatureMiniCard
                                icon={<LayoutPanelLeft />}
                                title="Ticket Management"
                                description="Organize and resolve customer queries with an efficient ticketing system."
                            >
                                <TicketMockup />
                            </FeatureMiniCard>
                        </div>

                        {/* NEW: AI Agents Card */}
                        <div className="bg-white/80 backdrop-blur-sm border border-white rounded-[2.5rem] p-4 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_60px_rgba(59,130,246,0.15)] hover:border-blue-200 transition-all duration-500 group relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-600"></div>

                            <div className="flex flex-col lg:flex-row items-center gap-6 md:gap-12">
                                {/* Agent Interaction Visual */}
                                <div className="w-full lg:w-[45%] flex-shrink-0 h-[300px]">
                                    <h3 className="text-lg md:text-2xl font-bold text-slate-900 mb-4 md:mb-6 text-center flex items-center justify-center gap-2">
                                        <Cpu className="w-5 h-5 text-blue-600" />
                                        Agent Creator
                                    </h3>

                                    <div className="bg-slate-100 rounded-2xl p-3 md:p-4 h-[180px] sm:h-[220px] shadow-inner relative overflow-hidden border border-slate-200">
                                        {/* Terminal Header */}
                                        <div className="flex justify-between items-center mb-4">
                                            <div className="flex gap-1.5">
                                                <div className="w-2 h-2 rounded-full bg-slate-200" />
                                                <div className="w-2 h-2 rounded-full bg-slate-200" />
                                                <div className="w-2 h-2 rounded-full bg-slate-200" />
                                            </div>
                                            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-white border border-slate-200 rounded-md shadow-sm">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                                <span className="text-[7px] font-bold text-slate-500 uppercase tracking-wider">Studio Engine</span>
                                            </div>
                                        </div>

                                        {/* Prompt Typing */}
                                        <div className="font-mono text-[9px] md:text-[10px] space-y-2 md:space-y-3">
                                            <div className="flex gap-2 text-blue-600">
                                                <span className="font-bold">→</span>
                                                <span className="text-slate-800 font-medium">{typingText}<span className="w-1.5 h-3 bg-blue-500 inline-block align-middle ml-0.5 animate-pulse" /></span>
                                            </div>

                                            {agentStatus !== "typing" && (
                                                <div>
                                                    <div className="text-blue-600 font-bold flex items-center gap-2">
                                                        <Sparkles className="w-3 h-3" />
                                                        <span>Configuring autonomous logic...</span>
                                                    </div>
                                                </div>
                                            )}

                                            {agentStatus === "active" && (
                                                <div className="mt-2 md:mt-4 p-2 md:p-3 bg-white border border-blue-100 rounded-xl shadow-[0_4px_12px_rgba(59,130,246,0.08)]">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div className="p-1 px-1.5 bg-blue-600 rounded text-[7px] text-white font-black uppercase tracking-widest">Active</div>
                                                            <span className="text-slate-900 font-extrabold">{agent.title}</span>
                                                        </div>
                                                        <div className="h-4 w-4 rounded-full bg-blue-50 flex items-center justify-center">
                                                            <div className="h-1.5 w-1.5 rounded-full bg-blue-600 animate-ping" />
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-1 mt-1.5 md:gap-1.5 md:mt-2.5">
                                                        {agent.steps.map((s, i) => (
                                                            <div key={i} className="px-2 py-0.5 bg-slate-50 border border-slate-100 rounded-md text-[7px] text-slate-500 font-bold uppercase">{s}</div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Glare effect */}
                                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />
                                    </div>
                                </div>

                                <div className="flex-1 flex flex-col gap-6 text-left">
                                    <div className="flex flex-col gap-4">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-[10px]">Autonamous Agents</span>
                                            <h4 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">AI Multi-Agent System</h4>
                                        </div>
                                        <p className="text-slate-600 text-sm md:text-lg leading-relaxed">
                                            Instantly deploy specialized AI Agents by providing simple natural language instructions. From refunds to wholesale management, agents handle complex logic autonomously.
                                        </p>
                                    </div>
                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                                        <BenefitItem label="Natural Language Setup" />
                                        <BenefitItem label="Autonomous Logic Execution" />
                                        <BenefitItem label="Shopify & CRM Integration" />
                                        <BenefitItem label="Real-time Admin Notifications" />
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* API Connectivity Card */}
                        <div className="bg-white/80 backdrop-blur-sm border border-white rounded-[2.5rem] p-4 md:p-6 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_60px_rgba(59,130,246,0.15)] hover:border-blue-200 transition-all duration-500 group relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-blue-600"></div>
                            <div className="flex flex-col md:flex-row items-center gap-10">
                                <div className="flex-1 flex flex-col gap-4 text-left">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-emerald-600 font-bold uppercase tracking-[0.2em] text-[10px]">Integrations</span>
                                        <h4 className="text-xl md:text-2xl font-extrabold text-slate-900 tracking-tight">API Connectivity</h4>
                                    </div>
                                    <p className="text-sm md:text-base leading-relaxed text-slate-600 max-w-2xl">
                                        We provide robust API connectivity to any system, including a development environment to write code and connect to any platform or custom solution with ease.
                                    </p>
                                </div>
                                <div className="w-full md:w-auto flex flex-wrap justify-center gap-4 bg-slate-100 p-6 rounded-2xl border border-slate-200">
                                    {integrationIcons.map((item, i) => (
                                        <div key={i} className="w-12 h-12 rounded-xl bg-white shadow-sm border border-slate-100 flex items-center justify-center overflow-hidden hover:scale-110 hover:shadow-md transition-all duration-300" title={item.label}>
                                            <img src={item.src} alt={item.label} className="w-full h-full object-cover" />
                                        </div>
                                    ))}
                                    <div className="w-12 h-12 rounded-xl bg-blue-600 text-white shadow-sm flex items-center justify-center hover:scale-110 hover:shadow-md transition-all duration-300 cursor-pointer">
                                        <Code2 className="w-6 h-6" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Omnichannel Social Hub Card */}
                        <div className="bg-white/80 backdrop-blur-sm border border-white rounded-[2.5rem] p-4 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_60px_rgba(59,130,246,0.15)] hover:border-blue-200 transition-all duration-500 group relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-600"></div>
                            <div className="flex flex-col lg:flex-row-reverse items-center gap-6 md:gap-12">
                                <div className="w-full lg:w-[45%] flex-shrink-0">
                                    <h3 className="text-lg md:text-2xl font-bold text-slate-900 mb-2 md:mb-8 text-center flex items-center justify-center gap-2">
                                        <Inbox className="w-5 h-5 text-blue-600" />
                                        Unified Inbox
                                    </h3>
                                    <div className="bg-slate-100 rounded-2xl p-1.5 md:p-3 h-[210px] sm:h-[300px] md:h-[340px] border border-slate-200 relative overflow-hidden flex flex-col gap-1 md:gap-1.5 font-sans">
                                        {socialChatsData.map((chat, i) => {
                                            const isActive = i === activeChat;
                                            return (
                                                <div
                                                    key={i}
                                                    className={`p-1 md:p-2 rounded-xl border transition-all duration-500 flex gap-1 md:gap-2.5 ${isActive ? 'bg-white border-blue-200 shadow-sm scale-102 z-10' : 'bg-white/40 border-slate-100 opacity-30 scale-90'}`}
                                                >
                                                    <div className={`w-5 h-5 md:w-7 md:h-7 rounded-full flex items-center justify-center bg-white shadow-sm border border-slate-100 ${chat.color}`}>
                                                        {React.cloneElement(chat.icon, { className: "w-2.5 h-2.5 md:w-3.5 md:h-3.5" })}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex justify-between items-center mb-0.5">
                                                            <span className="text-[8px] md:text-[9px] font-black text-slate-900 truncate">{chat.user}</span>
                                                            <span className="text-[7px] md:text-[8px] font-medium text-slate-400">{chat.time}</span>
                                                        </div>
                                                        <p className="text-[8px] md:text-[9px] text-slate-500 truncate leading-none">{chat.message}</p>
                                                    </div>
                                                    {isActive && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse mt-1" />}
                                                </div>
                                            );
                                        })}
                                        <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-slate-50/90 via-slate-50/20 to-transparent pointer-events-none" />
                                    </div>
                                </div>
                                <div className="flex-1 flex flex-col gap-6 text-left">
                                    <div className="flex flex-col gap-4">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-[10px]">Omnichannel</span>
                                            <h4 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">Social Media Hub</h4>
                                        </div>
                                        <p className="text-slate-600 text-sm md:text-lg leading-relaxed">Centralize all customer conversations from WhatsApp, Instagram, Messenger, Gmail, and Facebook into a single, AI-powered control center.</p>
                                    </div>
                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                                        {["Unified Chat Dashboard", "Cross-Platform Sync", "Instant Response AI", "Consolidated Analytics"].map(b => <BenefitItem key={b} label={b} />)}
                                    </ul>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section >
    );
};

const DiagramPlaceholder = () => (
    <div className="relative h-[160px] md:h-[240px] flex items-center justify-center">
        <style>
            {`
                @keyframes float-simple {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-8px); }
                }
                @keyframes float-bot {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-6px); }
                }
                @keyframes pulse-ring {
                    0% { transform: scale(0.5); opacity: 0.9; }
                    100% { transform: scale(3.5); opacity: 0; }
                }
            `}
        </style>
        <svg className="absolute inset-0 w-full h-full z-[15] pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M 50 12 L 50 38 M 88 20 Q 68 32 57 43 M 88 80 Q 68 68 57 57 M 50 88 L 50 62 M 12 80 Q 32 68 43 57 M 12 20 Q 32 32 43 43" stroke="#3b82f6" strokeWidth="1" fill="none" opacity="0.3" strokeDasharray="4 2" />
        </svg>

        {/* Central Bot Icon with animation */}
        <div className="relative z-20">
            <div className="relative bg-gradient-to-br from-blue-600 to-blue-500 p-4 md:p-5 rounded-full shadow-[0_15px_30px_rgba(59,130,246,0.3)] animate-[float-bot_3s_infinite_ease-in-out]">
                <Bot className="h-8 w-8 md:h-10 md:w-10 text-white" />
            </div>
        </div>

        <FeatureTag pos="top-0 left-1/2 -translate-x-1/2" icon={<Database />} label="Knowledge" delay="0s" floating />
        <FeatureTag pos="top-[15%] right-[2%]" icon={<MessageSquare />} label="Smart Chat" delay="0.5s" floating />
        <FeatureTag pos="bottom-[15%] right-[2%]" icon={<Globe />} label="API" delay="1s" floating />
        <FeatureTag pos="bottom-0 left-1/2 -translate-x-1/2" icon={<Zap />} label="Sync" delay="1.5s" floating />
        <FeatureTag pos="bottom-[15%] left-[2%]" icon={<Headphones />} label="Support" delay="2s" floating />
        <FeatureTag pos="top-[15%] left-[2%]" icon={<Users />} label="Global" delay="2.5s" floating />
    </div>
);

const FeatureTag = ({ pos, icon, label, delay = "0s", floating = false }) => (
    <div className={`absolute ${pos} z-10`}>
        <div
            className="transition-all duration-300"
            style={floating ? { animation: `float-simple 4s infinite ease-in-out ${delay}` } : {}}
        >
            <div className="bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm flex items-center gap-1.5 hover:shadow-md hover:border-blue-200 transition-all">
                {React.cloneElement(icon, { className: "w-3 h-3 text-blue-600" })}
                <span className="text-[10px] font-bold text-slate-700 whitespace-nowrap">{label}</span>
            </div>
        </div>
    </div>
);

const BenefitItem = ({ label }) => (
    <li className="flex items-start gap-1.5 md:gap-2.5">
        <div className="mt-0.5 md:mt-1 w-4 h-4 md:w-5 md:h-5 rounded bg-blue-50 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 text-blue-600" />
        </div>
        <span className="text-slate-700 font-medium text-xs md:text-base leading-tight">{label}</span>
    </li>
);

const WorkflowBadge = ({ label, active }) => (
    <div className={`px-1.5 md:px-2 py-0.5 md:py-1 rounded-full text-[7px] md:text-[8px] font-bold transition-all duration-300 border ${active ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-500'}`}>
        {label}
    </div>
);

const Node = ({ icon, label, active }) => (
    <div className={`w-full bg-white border-2 ${active ? 'border-blue-500 shadow-md' : 'border-slate-100'} rounded-xl py-1.5 md:py-2 px-1 flex flex-col items-center justify-center min-h-[45px] md:min-h-[50px]`}>
        {active && <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-blue-500 rounded-full border border-white z-20" />}
        <div className={`p-1.5 rounded-lg mb-1 ${active ? 'bg-blue-50 text-blue-600' : 'text-slate-300'}`}>
            {React.cloneElement(icon, { className: "w-3.5 h-3.5 md:w-4 md:h-4" })}
        </div>
        <span className={`text-[8px] md:text-[9px] font-bold ${active ? 'text-slate-900' : 'text-slate-400'} text-center leading-tight`}>{label}</span>
        {active && <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-blue-500 rounded-full border border-white z-20" />}
    </div>
);

const FeatureMiniCard = ({ icon, title, description, children }) => (
    <div className="relative bg-white rounded-3xl p-6 md:p-7 border border-slate-100 shadow-[0_10px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_18px_40px_rgba(59,130,246,0.12)] transition-all duration-500 flex flex-col overflow-hidden">
        <div className="text-blue-600 mb-4">
            {React.cloneElement(icon, { className: "w-6 h-6" })}
        </div>
        <h3 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-2 tracking-tight">{title}</h3>
        <p className="text-slate-500 text-sm md:text-[15px] leading-relaxed mb-6">{description}</p>
        <div className="mt-auto relative h-[260px]">
            {children}
        </div>
    </div>
);

const ChatBubbleUser = ({ children }) => (
    <div className="self-end flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-4 pr-1 py-1 shadow-sm max-w-[90%]">
        <span className="text-[12px] font-semibold text-slate-900 whitespace-nowrap">{children}</span>
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-200 via-orange-200 to-amber-100 border border-white shadow-sm flex-shrink-0 overflow-hidden">
            <div className="w-full h-full bg-[radial-gradient(circle_at_50%_30%,rgba(255,255,255,0.6),transparent_60%)]" />
        </div>
    </div>
);

const ChatBubbleAI = ({ children }) => (
    <div className="self-start flex items-center gap-2 bg-slate-100 rounded-full pl-1 pr-4 py-1 max-w-[95%]">
        <div className="w-7 h-7 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-white" />
        </div>
        <span className="text-[12px] font-semibold text-slate-900">{children}</span>
    </div>
);

const DataSyncMockup = () => (
    <div className="absolute inset-0 flex flex-col gap-2.5 pb-2">
        <ChatBubbleUser>What's the status of my order ?</ChatBubbleUser>
        <ChatBubbleAI>Here's is your order status :</ChatBubbleAI>

        <div className="flex-1 relative mt-1">
            <div className="absolute left-3 top-0 bottom-12 w-px border-l border-dashed border-slate-300" />
            <div className="absolute left-3 right-12 bottom-12 h-px border-t border-dashed border-slate-300" />

            <div className="absolute left-2 right-2 bottom-0 flex items-stretch gap-2">
                <div className="flex-1 bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
                    <div className="flex justify-between items-center mb-1.5">
                        <span className="text-[11px] font-bold text-slate-900">Out for delivery</span>
                        <span className="text-[10px] text-slate-500">Arriving Sunday</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full w-3/4 bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500" />
                    </div>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 via-blue-500 to-blue-700 flex flex-col items-center justify-center text-white shadow-md shrink-0">
                    <Webhook className="w-4 h-4" />
                    <span className="text-[7px] font-bold mt-0.5 tracking-wider">API</span>
                </div>
            </div>
        </div>

        <div className="absolute -bottom-6 left-2 right-2 h-3 bg-gradient-to-r from-emerald-300 via-cyan-400 to-blue-500 blur-md opacity-70" />
    </div>
);

const AnalyticsMockup = () => (
    <div className="absolute inset-0 flex flex-col gap-2">
        <div className="relative bg-white border border-slate-200 rounded-2xl p-3 shadow-sm flex-1 overflow-hidden">
            <span className="text-[11px] font-bold text-slate-700">Topics</span>

            <svg className="absolute inset-x-0 top-8 w-full h-[70%]" viewBox="0 0 200 80" preserveAspectRatio="none">
                <path d="M -5 50 Q 35 18, 70 38 T 140 30 T 210 45" fill="none" stroke="#cbd5e1" strokeWidth="1.2" />
                <path d="M -5 60 Q 35 30, 70 50 T 140 22 T 210 38" fill="none" stroke="#e2e8f0" strokeWidth="1.2" />
                <path d="M -5 45 Q 35 12, 70 32 T 140 55 T 210 25" fill="none" stroke="#a78bfa" strokeWidth="2" />
                <circle cx="35" cy="22" r="2.5" fill="#fff" stroke="#a78bfa" strokeWidth="1.5" />
                <circle cx="140" cy="55" r="2.5" fill="#fff" stroke="#a78bfa" strokeWidth="1.5" />
            </svg>

            <div className="absolute right-2 top-8 flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                <div className="w-3 h-3 rounded bg-purple-500" />
                <span className="text-[10px] text-slate-600">Account setup</span>
                <span className="text-[10px] font-bold text-slate-900">643</span>
            </div>

            <MousePointer2
                className="absolute left-[42%] top-[55%] w-5 h-5 text-slate-900"
                style={{ fill: "#0f172a" }}
            />
        </div>

        <div className="flex justify-around bg-slate-50 rounded-2xl p-3 border border-slate-100">
            <div className="flex items-center gap-1.5">
                <ThumbsUp className="w-4 h-4 text-emerald-500" style={{ fill: "rgba(16,185,129,0.15)" }} />
                <span className="text-sm font-bold text-slate-900">94%</span>
                <span className="text-sm text-slate-500">Positive</span>
            </div>
            <div className="flex items-center gap-1.5">
                <ThumbsDown className="w-4 h-4 text-red-500" style={{ fill: "rgba(239,68,68,0.15)" }} />
                <span className="text-sm font-bold text-slate-900">4%</span>
                <span className="text-sm text-slate-500">Negative</span>
            </div>
        </div>
    </div>
);

const TicketMockup = () => (
    <div className="absolute inset-0 flex flex-col gap-2.5 pb-2">
        <ChatBubbleUser>Hey, I would like a refund</ChatBubbleUser>
        <ChatBubbleAI>Sure, let's create a ticket!</ChatBubbleAI>

        <div className="flex items-center gap-2 my-1 px-1">
            <div className="flex-1 h-px bg-slate-200" />
            <Ticket className="w-3 h-3 text-slate-400" />
            <span className="text-[10px] text-slate-400 font-medium">Ticket submitted</span>
            <div className="flex-1 h-px bg-slate-200" />
        </div>

        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
            <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Ticket className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1">
                <div className="text-[13px] font-extrabold text-slate-900">Ticket #234</div>
                <div className="text-[11px] text-slate-500">Open</div>
            </div>
            <span className="text-[10px] text-slate-400">12m ago</span>
        </div>

        <div className="absolute -bottom-6 left-2 right-2 h-3 bg-gradient-to-r from-cyan-300 via-blue-400 to-blue-600 blur-md opacity-70" />
    </div>
);

export default Services;
